/**
 * Hasil alur deterministik, di layar tempat alur itu dijalankan.
 *
 * Sampai kini tidak ada. Layar analisis memberi alur deterministik satu rel
 * kiri berisi enam kartu tahap, dan menyerahkan **seluruh** sisa layarnya —
 * kolom tengah dan rel QC — kepada penyelesai multi-mineral. Angka yang justru
 * menjadi alasan alur itu dijalankan, yaitu net pay tiap zona, hanya muncul
 * sebagai satu angka kecil di dalam kartu tahap keenam; ringkasan per zona,
 * HCPV, dan peringatan QC-nya tidak muncul di mana pun.
 *
 * Yang dicetak di sini karena itu bukan hitungan baru: seluruhnya sudah ada di
 * dalam `summary` dan `qc` milik run-nya, dan yang belum ada hanyalah tempat
 * membacanya.
 */
import type { AnalysisRun, ZoneSummary } from "../types";
import { Empty, KeyValue, Notice, Panel } from "./ui";

const cell = (align: "left" | "right" = "right", bold = false): React.CSSProperties => ({
  padding: "6px 9px",
  textAlign: align,
  font: `${bold ? 600 : 400} 10.5px var(--font-mono)`,
  color: bold ? "var(--ink)" : "var(--ink-2)",
  borderBottom: "1px solid var(--line)",
  whiteSpace: "nowrap",
});

const head: React.CSSProperties = {
  padding: "7px 9px",
  textAlign: "right",
  font: "600 9.5px var(--font-mono)",
  color: "var(--ink-2)",
  letterSpacing: ".06em",
  borderBottom: "1px solid var(--line-strong)",
};

/** Angka yang tidak ada dicetak sebagai "—", bukan sebagai nol.
 *
 *  Nol adalah sebuah pengukuran: "sudah dihitung, tidak ada". Zona yang
 *  kurvanya tidak sampai ke sana tidak mengatakan itu, dan menaruh nol di
 *  tempatnya membuat rerata sebuah lapangan ditarik turun oleh zona yang tidak
 *  pernah diperiksa siapa pun. */
const num = (value: number | null | undefined, digits: number): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : value.toFixed(digits);

const COLUMNS: { key: keyof ZoneSummary; label: string; digits: number }[] = [
  { key: "gross", label: "Gross", digits: 1 },
  { key: "net_reservoir", label: "Net res", digits: 1 },
  { key: "net_pay", label: "Net pay", digits: 1 },
  { key: "ntg", label: "N/G", digits: 3 },
  { key: "phie_avg", label: "PHIE", digits: 3 },
  { key: "sw_avg", label: "SW", digits: 2 },
  { key: "vsh_avg", label: "VSH", digits: 2 },
  { key: "perm_avg", label: "PERM", digits: 1 },
  { key: "hcpv", label: "HCPV", digits: 3 },
];

/**
 * Ringkasan tiap zona berikut barisnya sendiri untuk totalnya.
 *
 * Totalnya dibaca dari `summary.total` dan **tidak** dijumlahkan ulang di sini.
 * Rerata berbobot net pay dan HCPV yang dijumlahkan dari zonanya adalah dua
 * hitungan yang berbeda dari angka yang sama, dan dua tempat yang masing-masing
 * menghitungnya sendiri cepat atau lambat mencetak dua angka di layar yang
 * sama.
 */
export function DeterministicResults({ run }: { run: AnalysisRun | null }) {
  if (!run) {
    return <Empty>Run the workflow to see net pay, porosity and saturation per zone.</Empty>;
  }
  const zones = run.summary.zones ?? [];
  const total = run.summary.total;
  const interval = run.summary.interval;

  return (
    <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          border: "1px solid var(--border-control)",
          borderRadius: "var(--radius-panel)",
          overflow: "auto",
          boxShadow: "var(--shadow-panel)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--panel-header)" }}>
              <th style={{ ...head, textAlign: "left" }}>Zone</th>
              {COLUMNS.map((column) => (
                <th key={column.label} style={head}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zones.map((zone) => (
              <tr key={zone.name} style={{ background: "var(--panel)" }}>
                <td style={cell("left")}>{zone.name}</td>
                {COLUMNS.map((column) => (
                  <td key={column.label} style={cell()}>
                    {num(zone[column.key] as number | null, column.digits)}
                  </td>
                ))}
              </tr>
            ))}
            {total && (
              <tr style={{ background: "var(--panel-header)" }}>
                <td style={cell("left", true)}>Total</td>
                {COLUMNS.map((column) => (
                  <td key={column.label} style={cell("right", true)}>
                    {num(total[column.key] as number | null, column.digits)}
                  </td>
                ))}
              </tr>
            )}
            {zones.length === 0 && (
              <tr style={{ background: "var(--panel)" }}>
                <td style={cell("left")} colSpan={COLUMNS.length + 1}>
                  This run summed no zone.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
        {"Gross, net reservoir and net pay in metres; N/G, PHIE, SW and VSH as fractions; " +
          "PERM in mD as a net-pay-weighted geometric mean; HCPV in metres. " +
          "Averages are weighted by net pay, so a zone that pays nothing does not " +
          "pull the field average anywhere."}
        {interval ? ` Interval ${interval[0].toFixed(1)}–${interval[1].toFixed(1)} m.` : ""}
      </span>
    </div>
  );
}

/**
 * Rel kanan tab deterministik: apa yang perlu diperiksa sebelum angkanya dipakai.
 *
 * Peringatan lebih dulu, sebelum kurva yang terbaca dan sebelum selangnya. Satu
 * baris "PHIE melampaui PHIT di 412 sampel" mengubah arti seluruh tabel di
 * sebelahnya, dan yang ditaruh di bawah daftar kurva adalah baris yang dibaca
 * sesudah tabelnya telanjur dipercaya.
 */
export function DeterministicQc({ run }: { run: AnalysisRun | null }) {
  if (!run) return <Empty>Run the workflow to see its QC.</Empty>;
  const warnings = run.qc?.warnings ?? [];
  const used = run.summary.curves_used ?? [];

  return (
    <>
      <Panel title={`Warnings — ${warnings.length}`}>
        {warnings.length === 0 ? (
          <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-5)" }}>
            Nothing to report: every stage read the curves it needed.
          </span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {warnings.map((warning) => (
              <Notice key={warning}>{warning}</Notice>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="What it read">
        <KeyValue k="Curves" v={used.length ? used.join(", ") : "—"} />
        <KeyValue
          k="Interval"
          v={
            run.summary.interval
              ? `${run.summary.interval[0].toFixed(1)}–${run.summary.interval[1].toFixed(1)} m`
              : "—"
          }
        />
        <KeyValue k="Samples" v={run.summary.samples ?? "—"} />
        <KeyValue k="Zones summed" v={(run.summary.zones ?? []).length} />
      </Panel>

      <Panel title="Stages">
        {run.steps.map((step) => (
          <KeyValue
            key={step.n}
            k={`${step.n} · ${step.name}`}
            v={step.ok ? "ok" : "incomplete"}
          />
        ))}
      </Panel>
    </>
  );
}
